import { Badge } from '@/components/ui/shadcn/badge';
import { useTranslations } from '@/hooks/useTranslations';
import { settingLabel } from '@/lib/status-labels';
import { cn } from '@/lib/utils';
import type { OptionRow } from '../types';

/**
 * Badges for the admin-managed option lists.
 *
 * Colour and ordering come from the settings row itself, and the label resolves
 * through `settings.<group>.<value>` so a status an admin adds is localised without a
 * code change. Nothing here hardcodes a status value.
 */

const FALLBACK_CLASSES = 'bg-muted text-muted-foreground border-border';

function useOptionBadge(group: string, options: OptionRow[], value: string | null | undefined) {
    const { t } = useTranslations();

    if (!value) {
        return null;
    }

    const option = options.find((candidate) => candidate.value === value);

    return {
        label: settingLabel(t, group, value) || option?.label || value,
        className: option?.color ?? FALLBACK_CLASSES,
        icon: option?.icon,
    };
}

interface OptionBadgeProps {
    value: string | null | undefined;
    options: OptionRow[];
    className?: string;
}

export function StatusBadge({ value, options, className }: OptionBadgeProps) {
    const badge = useOptionBadge('calculation_request_statuses', options, value);

    if (!badge) {
        return null;
    }

    return (
        <Badge variant="outline" className={cn(badge.className, className)}>
            {badge.icon ? <span className="mr-1">{badge.icon}</span> : null}
            {badge.label}
        </Badge>
    );
}

export function PhaseBadge({ value, options, className }: OptionBadgeProps) {
    const badge = useOptionBadge('calculation_request_phases', options, value);

    if (!badge) {
        return null;
    }

    return (
        <Badge variant="outline" className={cn(badge.className, className)}>
            {badge.label}
        </Badge>
    );
}

export function ComplexityBadge({ value, options, className }: OptionBadgeProps) {
    const badge = useOptionBadge('calculation_request_complexities', options, value);

    if (!badge) {
        return null;
    }

    return (
        <Badge variant="outline" className={cn(badge.className, className)}>
            {badge.label}
        </Badge>
    );
}

const OUTCOME_CLASSES: Record<string, string> = {
    pending: 'bg-muted text-muted-foreground border-border',
    won: 'bg-success-subtle text-success-subtle-foreground border-transparent',
    lost: 'bg-danger-subtle text-danger-subtle-foreground border-transparent',
};

export function OutcomeBadge({ value, className }: { value: string | null | undefined; className?: string }) {
    const { t } = useTranslations();

    if (!value) {
        return null;
    }

    return (
        <Badge variant="outline" className={cn(OUTCOME_CLASSES[value] ?? FALLBACK_CLASSES, className)}>
            {t(`calculation_requests.outcomes.${value}`, value)}
        </Badge>
    );
}

export function OverdueBadge({ className }: { className?: string }) {
    const { t } = useTranslations();

    return (
        <Badge variant="outline" className={cn('bg-danger-subtle text-danger-subtle-foreground border-transparent', className)}>
            {t('calculation_requests.tables.overdue', 'Overdue')}
        </Badge>
    );
}

/** Full name for a user reference, falling back to an em dash. */
export function userName(user: { first_name?: string | null; last_name?: string | null } | null | undefined): string {
    if (!user) {
        return '—';
    }

    return [user.first_name, user.last_name].filter(Boolean).join(' ').trim() || '—';
}
